/**
 * contentTokens.ts — the tokens every translated string may reference.
 *
 * Locale bundles are pure data: they may hold `{country}` and `{supportPhone}`
 * but never a country name or a dialling code spelled out (issue #378 — the
 * ratchet in scripts/ scans the bundles for exactly that). This module is where
 * those tokens get their values, at render time, from whatever country config
 * is live — the admin-managed one when a `ContentOverridesProvider` is mounted,
 * the seeded `DEFAULT_COUNTRY` otherwise.
 *
 * `{brand}` is here for the same reason it is not in the bundles: a product
 * name is not translatable (src/lib/brand.ts).
 *
 * Callers may still pass their own vars to `t()`; those win over these
 * defaults, which is how per-render values like `{count}` or `{query}` work.
 */
import { BRAND_NAME } from '@/lib/brand';
import { DEFAULT_COUNTRY, type CountryConfig } from '@/lib/countryConfig';
import { DEFAULT_CITY_FALLBACK } from '@/lib/countryGeo';

export type ContentTokens = Record<string, string>;

/**
 * Build the default token set for a country config.
 *
 * @param config country config to read country-specific values from
 * @param cities city names for the `{city1}`/`{city2}` examples, most
 *   significant first; omit the argument to use the seeded default list
 */
export function buildContentTokens(
  config: CountryConfig = DEFAULT_COUNTRY,
  cities: readonly string[] = DEFAULT_CITY_FALLBACK,
): ContentTokens {
  const cityList = cities;
  return {
    brand: BRAND_NAME,
    country: config.name,
    countryCode: config.code,
    currency: config.currencyCode,
    supportPhone: config.support.phone,
    supportWhatsapp: config.support.whatsapp,
    supportLocation: config.support.location,
    city1: cityList[0] ?? '',
    city2: cityList[1] ?? cityList[0] ?? '',
  };
}

/** Token set for the seeded default country — used when no provider is mounted. */
export const DEFAULT_CONTENT_TOKENS: ContentTokens = buildContentTokens();
